import firebase from "firebase/app";
import "firebase/auth";
import "firebase/firestore";
import moment from "moment";

import { getFirebaseBackend } from "../authUtils";

/**
 * Alta de cuentas desde el portal web.
 *
 * Crea exactamente la misma estructura que la app móvil
 * (`recnet-24/app/screens/SignUp/index.js`): cuenta de Firebase Auth + documento
 * en `app_usuarios`. Así la misma persona puede entrar por la web y por la app.
 *
 * No se registra en la colección `usuarios` (modelo viejo que usa la web
 * anterior) ni se crea el cliente de Stripe aquí: eso ocurre sólo cuando el
 * usuario va a agregar una tarjeta, igual que en la app.
 */

const POOL = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ";

/** Código de referido con el formato de la app: XXXX-XXXX-XXXX. */
export function generarCodigoReferido() {
  const parte = () =>
    Array.from({ length: 4 }, () => POOL[Math.floor(Math.random() * POOL.length)]).join("");
  return `${parte()}-${parte()}-${parte()}`;
}

/**
 * Documento base de un alta. `tipo` es "normal" | "comercio"; el resto replica
 * lo que escribe la app al registrarse.
 */
function documentoDeAlta(email, signinMethod, tipo) {
  return {
    email: email.toLowerCase(),
    creation_timestamp: moment().format("x"),
    city: "chihuahua",
    referral_code: generarCodigoReferido(),
    permission: "user",
    status: "signup_pending",
    signin_method: signinMethod,
    type: tipo === "comercio" ? "comercio" : "normal",
  };
}

/**
 * Registro con correo y contraseña.
 *
 * Si escribir el documento falla después de crear la cuenta de Auth, se borra
 * la cuenta: de lo contrario el correo queda bloqueado por
 * `auth/email-already-in-use` y el registro sería irrepetible.
 */
export async function registrarConCorreo({ email, password, tipo }) {
  const correo = email.toLowerCase().trim();

  await firebase.auth().createUserWithEmailAndPassword(correo, password);

  try {
    await firebase.firestore().collection("app_usuarios").add(documentoDeAlta(correo, "email", tipo));
  } catch (err) {
    await rollbackAuth();
    throw err;
  }

  const usuario = firebase.auth().currentUser;
  if (usuario) getFirebaseBackend().setLoggeedInUser(usuario);
  return usuario;
}

/**
 * Registro (o entrada) con Google.
 *
 * Canjea el token de identidad y, si el correo no tiene documento en
 * `app_usuarios`, lo crea. Si ya existe, no se duplica: equivale a iniciar
 * sesión con una cuenta que se registró antes.
 */
export async function registrarConGoogle({ idToken, tipo }) {
  const usuario = await getFirebaseBackend().loginWithGoogleCredential(idToken);
  if (!usuario || !usuario.email) {
    throw new Error("Google no devolvió un correo.");
  }

  const correo = usuario.email.toLowerCase();
  const existentes = await firebase
    .firestore()
    .collection("app_usuarios")
    .where("email", "==", correo)
    .limit(1)
    .get();

  if (existentes.empty) {
    await firebase.firestore().collection("app_usuarios").add(documentoDeAlta(correo, "google", tipo));
  }

  return usuario;
}

/** Borra la cuenta de Auth recién creada cuando el alta no se pudo completar. */
async function rollbackAuth() {
  try {
    const usuario = firebase.auth().currentUser;
    if (usuario) await usuario.delete();
  } catch (err) {
    // Si ni siquiera se puede borrar, se deja constancia: el correo podría
    // quedar en un estado a medias que soporte tendría que limpiar.
    console.error("[registro] no se pudo revertir la cuenta de Auth:", err);
  }
}
